// ModernCursorEditorPanel — Modern の「カーソル」エディタ (custom-cursor-2026-09-19.md
// §2.5)。中身は Classic とも共有する `CursorEditorPanel`
// (presentation/layouts/shared/)。
//
// custom-cursor-ux フォローアップ (2026-09-19、作者 staging フィードバック §B「Modern の
// エディタをドラッグで動かしたい」): 完全な `panelRegistry`/`PanelId`/`FloatShell` 統合は
// 検討した上で見送った (= 波及範囲が広い上、ephemeral な一時窓と永続 workspace パネルの
// 意味論が噛み合わない。詳細理由は `modernCursorEditorPanel.ts` 冒頭コメント参照)。
// 代わりに Classic `CursorEditorPopup.tsx` と同型の軽量フロート chrome にした:
// `usePanelDragGrip` + `shared/floatShellGeometry.ts` を使ってヘッダを掴んでドラッグでき、
// viewport 内にクランプし、位置を localStorage に記憶する (`modernCursorEditorPanel.ts`)。
// 独自のドラッグ実装は書かず、既存の共有 geometry をそのまま使う。

import type { CustomCursor } from '@oekaki/protocol';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CursorEditorPanel } from '../shared/CursorEditorPanel.js';
import {
  clampFloatPosition,
  type FloatPoint,
  floatPositionFromPointer,
} from '../shared/floatShellGeometry.js';
import { useCursorEditorCellPxResize } from '../shared/useCursorEditorCellPxResize.js';
import { usePanelDragGrip } from '../shared/usePanelDragGrip.js';
import {
  defaultModernCursorEditorPanelPosition,
  MODERN_CURSOR_EDITOR_PANEL_NOMINAL_HEIGHT,
  MODERN_CURSOR_EDITOR_PANEL_NOMINAL_WIDTH,
  readModernCursorEditorPanelPosition,
  writeModernCursorEditorPanelPosition,
} from './modernCursorEditorPanel.js';

/** 旧 `clampPopupPosition` の既定 margin (8px) と揃える (= Classic CursorEditorPopup と同じ). */
const POPUP_EDGE_INSET_PX = 8;

export interface ModernCursorEditorPanelProps {
  readonly onClose: () => void;
  readonly initialCursor: CustomCursor | null;
  readonly currentColor: { readonly rgb24: number; readonly alpha: number };
  readonly spuitMode: boolean;
  readonly onSpuitPick: (color: { rgb24: number; alpha: number }) => void;
  readonly onDraft: (cursor: CustomCursor | null) => void;
  readonly onConfirm: (cursor: CustomCursor) => void;
  readonly onRemove: () => void;
  /**
   * 自分のカーソルを自分の画面にも表示するか (作者 staging フィードバック
   * 2026-09-19 follow-up、`PenSettingsPanel` と同じ共有 chrome 値)。省略時は
   * `CursorEditorPanel` 側の既定 (= チェックボックス自体を出さない) に従う。
   */
  readonly showOwnCustomCursor?: boolean;
  readonly onShowOwnCustomCursorChange?: (next: boolean) => void;
}

export const ModernCursorEditorPanel = ({
  onClose,
  initialCursor,
  currentColor,
  spuitMode,
  onSpuitPick,
  onDraft,
  onConfirm,
  onRemove,
  showOwnCustomCursor,
  onShowOwnCustomCursorChange,
}: ModernCursorEditorPanelProps): React.ReactNode => {
  // 作者 staging フィードバック 2026-09-19 follow-up「パネルをリサイズして大きく
  // したい」: プリセットボタン (小/中/大、CursorEditorPanel 内) とこの panel の
  // 右下角ドラッグの両方が同じ `cellPx` state を更新する (= 1 本の共有 hook、
  // Classic `CursorEditorPopup` と同じもの).
  const { cellPx, setCellPx, resizeGrip } = useCursorEditorCellPxResize();
  const [pos, setPos] = useState<FloatPoint>(() => {
    if (typeof window === 'undefined') return defaultModernCursorEditorPanelPosition(0, 0);
    const stored = readModernCursorEditorPanelPosition(window.localStorage);
    if (stored !== null) return stored;
    return defaultModernCursorEditorPanelPosition(window.innerWidth, window.innerHeight);
  });
  const panelRef = useRef<HTMLDivElement | null>(null);

  const measurePanel = useCallback((): { width: number; height: number } => {
    const el = panelRef.current;
    if (el !== null) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return { width: r.width, height: r.height };
    }
    return {
      width: MODERN_CURSOR_EDITOR_PANEL_NOMINAL_WIDTH,
      height: MODERN_CURSOR_EDITOR_PANEL_NOMINAL_HEIGHT,
    };
  }, []);

  const persistPosition = useCallback((next: FloatPoint): void => {
    if (typeof window === 'undefined') return;
    writeModernCursorEditorPanelPosition(window.localStorage, next);
  }, []);

  const grabOffsetRef = useRef<FloatPoint | null>(null);
  const grip = usePanelDragGrip({
    onDragStart: (offset) => {
      grabOffsetRef.current = offset;
    },
    onDragMove: (clientX, clientY) => {
      const offset = grabOffsetRef.current ?? { x: 0, y: 0 };
      setPos(
        clampFloatPosition(
          floatPositionFromPointer(clientX, clientY, offset),
          measurePanel(),
          { width: window.innerWidth, height: window.innerHeight },
          POPUP_EDGE_INSET_PX,
        ),
      );
    },
    onDragEnd: (committed) => {
      grabOffsetRef.current = null;
      if (committed) {
        setPos((cur) => {
          persistPosition(cur);
          return cur;
        });
      }
    },
    measureGrabOffset: (e) => {
      const rect = panelRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    },
  });

  // 初回 clamp: 復元位置が小さい画面ではみ出すケースを mount 後に画面内へ引き戻す
  // (Classic CursorEditorPopup と同じ流儀)。
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setPos((cur) => {
      const clamped = clampFloatPosition(
        cur,
        measurePanel(),
        { width: window.innerWidth, height: window.innerHeight },
        POPUP_EDGE_INSET_PX,
      );
      if (clamped.x !== cur.x || clamped.y !== cur.y) persistPosition(clamped);
      return clamped;
    });
  }, [measurePanel, persistPosition]);

  const handleConfirm = (cursor: CustomCursor): void => {
    onConfirm(cursor);
    onClose();
  };
  const handleRemove = (): void => {
    onRemove();
    onClose();
  };

  return (
    // biome-ignore lint/a11y/useSemanticElements: 非モーダルの浮動パネル (FloatShell と同じ role="region" 慣用).
    <div
      ref={panelRef}
      role="region"
      aria-label="カーソル"
      data-testid="modern-cursor-editor-panel"
      className="fixed z-40 flex min-w-[280px] flex-col gap-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] p-2 shadow-xl"
      style={{ left: pos.x, top: pos.y }}
    >
      {/* ヘッダ = 掴み所 (Classic CursorEditorPopup と同じ `usePanelDragGrip` 配管)。
          × ボタンだけは pointerdown を素通りさせる必要が無い (= grip 領域外)。 */}
      <div
        data-testid="modern-cursor-editor-panel-header"
        className="flex cursor-move items-center justify-between"
        style={{ touchAction: 'none' }}
        onPointerDown={grip.onPointerDown}
        onPointerMove={grip.onPointerMove}
        onPointerUp={grip.onPointerUp}
        onPointerCancel={grip.onPointerCancel}
        onLostPointerCapture={grip.onLostPointerCapture}
      >
        <span className="select-none text-[11px] font-bold text-[var(--color-text-muted)]">
          カーソル
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="カーソルエディタを閉じる"
          // ヘッダの pointerdown (grip) より先に click を受け取れるよう、grip への委譲を
          // 止める (ColorWheelPalette の × ボタンと同じ既知パターン)。
          onPointerDown={(e) => e.stopPropagation()}
          className="rounded px-1 text-[11px] text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-white"
        >
          ×
        </button>
      </div>
      <CursorEditorPanel
        initialCursor={initialCursor}
        currentColor={currentColor}
        spuitMode={spuitMode}
        onSpuitPick={onSpuitPick}
        onDraft={onDraft}
        onConfirm={handleConfirm}
        onCancel={onClose}
        onRemove={handleRemove}
        cellPx={cellPx}
        onCellPxChange={setCellPx}
        showOwnCustomCursor={showOwnCustomCursor}
        onShowOwnCustomCursorChange={onShowOwnCustomCursorChange}
      />
      {/* 作者 staging フィードバック 2026-09-19 follow-up「パネルをリサイズして
          大きくしたい」: 右下角ドラッグでグリッドを拡縮する (= Classic
          `CursorEditorPopup` と同じ `useCursorEditorCellPxResize` + `usePanelDragGrip`). */}
      <div
        data-testid="modern-cursor-editor-panel-resize-grip"
        role="presentation"
        title="ドラッグでマスの大きさを変更"
        className="-right-1 -bottom-1 absolute h-4 w-4 cursor-nwse-resize opacity-60 hover:opacity-100"
        style={{
          touchAction: 'none',
          backgroundImage:
            'linear-gradient(135deg, transparent 0 45%, var(--color-border) 45% 55%, transparent 55% 100%), linear-gradient(135deg, transparent 0 70%, var(--color-border) 70% 80%, transparent 80% 100%)',
        }}
        onPointerDown={resizeGrip.onPointerDown}
        onPointerMove={resizeGrip.onPointerMove}
        onPointerUp={resizeGrip.onPointerUp}
        onPointerCancel={resizeGrip.onPointerCancel}
        onLostPointerCapture={resizeGrip.onLostPointerCapture}
      />
    </div>
  );
};
